import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { toast } from "react-toastify";

import API from "../utils/api";
import useAuth from "../hooks/useAuth";

const SMS_SOFT_LIMIT = 160;
const SMS_HARD_LIMIT = 320;
const EMPTY_EMAILS = [
  { subject: "", body: "" },
  { subject: "", body: "" },
  { subject: "", body: "" },
];

function emailsFromPayload(data) {
  const incoming = Array.isArray(data?.followup_emails) ? data.followup_emails : [];
  return EMPTY_EMAILS.map((fallback, index) => ({
    subject: incoming[index]?.subject || fallback.subject,
    body: incoming[index]?.body || fallback.body,
  }));
}

function SectionCard({ icon, title, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.25 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        bgcolor: isDark ? "rgba(255,255,255,0.025)" : "rgba(248,250,252,0.72)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDark ? "rgba(59,130,246,0.14)" : "rgba(59,130,246,0.09)",
            color: "primary.main",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

function ChannelSwitch({ label, description, checked, disabled, onChange }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
      <Box sx={{ minWidth: 0, pr: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    </Stack>
  );
}

export default function FollowUpSettingsPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { role, isReadOnly } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailReady, setEmailReady] = useState(false);
  const [smsReady, setSmsReady] = useState(false);
  const [autoEmail, setAutoEmail] = useState(false);
  const [autoSms, setAutoSms] = useState(false);
  const [emails, setEmails] = useState(EMPTY_EMAILS);
  const [emailTab, setEmailTab] = useState(0);
  const [smsBody, setSmsBody] = useState("");
  const [setupPrompt, setSetupPrompt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    API.get("intelligence/followup-settings/")
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setAutoEmail(!!data.followup_auto_email_enabled);
        setAutoSms(!!data.followup_auto_sms_enabled);
        setEmails(emailsFromPayload(data));
        setSmsBody(data.followup_sms_body || "");
        setEmailReady(!!data.email_ready);
        setSmsReady(!!data.sms_ready);
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load follow-up settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateEmail = (index, field, value) => {
    setEmails((current) =>
      current.map((email, emailIndex) =>
        emailIndex === index ? { ...email, [field]: value } : email
      )
    );
  };

  const requestChannel = (channel, next) => {
    if (isReadOnly) return;
    if (!next) {
      if (channel === "email") setAutoEmail(false);
      else setAutoSms(false);
      return;
    }
    const ready = channel === "email" ? emailReady : smsReady;
    const alreadyOn = channel === "email" ? autoEmail : autoSms;
    if (!ready && !alreadyOn) {
      setSetupPrompt(channel);
      return;
    }
    if (channel === "email") setAutoEmail(true);
    else setAutoSms(true);
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    if (emails.some((email) => !email.subject.trim() || !email.body.trim()) || !smsBody.trim()) {
      toast.error("Add a subject and message for each email, and a text message, before saving.");
      return;
    }
    if (smsBody.length > SMS_HARD_LIMIT) {
      toast.error("The text message must be 320 characters or fewer.");
      return;
    }
    setSaving(true);
    try {
      const res = await API.patch("intelligence/followup-settings/", {
        followup_auto_email_enabled: autoEmail,
        followup_auto_sms_enabled: autoSms,
        followup_emails: emails,
        followup_sms_body: smsBody,
      });
      const data = res.data || {};
      setAutoEmail(!!data.followup_auto_email_enabled);
      setAutoSms(!!data.followup_auto_sms_enabled);
      setEmails(emailsFromPayload(data));
      setSmsBody(data.followup_sms_body || "");
      setEmailReady(!!data.email_ready);
      setSmsReady(!!data.sms_ready);
      toast.success("Follow-up settings saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("integrations")) {
        setSetupPrompt(detail.toLowerCase().includes("sms") ? "sms" : "email");
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Couldn't save follow-up settings.");
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.25,
      bgcolor: "background.paper",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        background: isDark
          ? "radial-gradient(circle at top, rgba(37,99,235,0.10), transparent 28%), linear-gradient(180deg, #0b1220 0%, #0f172a 100%)"
          : "radial-gradient(circle at top, rgba(37,99,235,0.08), transparent 24%), linear-gradient(180deg, #f8fafc 0%, #f4f7fb 100%)",
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 860, mx: "auto" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/follow-up-intelligence")}
          sx={{ textTransform: "none", fontWeight: 700, mb: 2, color: "text.secondary" }}
        >
          Follow-Up Intelligence
        </Button>

        <Box
          sx={{
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
            bgcolor: isDark ? "rgba(15,23,42,0.72)" : "#fff",
            boxShadow: isDark ? "0 10px 28px rgba(0,0,0,0.24)" : "0 10px 28px rgba(15,23,42,0.06)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDark ? "rgba(59,130,246,0.14)" : "rgba(59,130,246,0.09)",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                <TuneIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  Follow-up settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Choose how the system contacts a customer when a follow-up is missed, and the words they see.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            <SectionCard icon={<TuneIcon sx={{ fontSize: 16 }} />} title="Automatic Proposal Follow-Up">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                When enabled, the system automatically follows up with customers who have not accepted their proposal. Emails are sent 1, 2, and 3 weeks after the proposal is delivered, with each message gradually increasing urgency as the 30-day offer expiration approaches.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                These professionally written emails remind customers to review their proposal, address any remaining questions, and encourage them to accept before their offer expires. Follow-ups stop as soon as the proposal is accepted, declined, or withdrawn. Editing it and sending it again starts the 30 days over.
              </Typography>
              <Stack spacing={2}>
                <ChannelSwitch
                  label="Auto email"
                  description={
                    emailReady || autoEmail
                      ? "Sends three emails, one week apart, when outgoing email is set up in Integrations."
                      : "Outgoing email is not set up, so automatic emails stay off until an Admin sets that up in Integrations."
                  }
                  checked={autoEmail}
                  disabled={loading || isReadOnly}
                  onChange={(next) => requestChannel("email", next)}
                />
                <ChannelSwitch
                  label="Auto SMS"
                  description={
                    smsReady || autoSms
                      ? "Sends the text when SMS is set up. If both are on, a customer who agreed to texts and has a phone number gets the text. Everyone else with an email gets the email."
                      : "SMS is not set up, so automatic texts stay off until an Admin sets that up in Integrations."
                  }
                  checked={autoSms}
                  disabled={loading || isReadOnly}
                  onChange={(next) => requestChannel("sms", next)}
                />
              </Stack>
            </SectionCard>

            <SectionCard icon={<MailOutlineIcon sx={{ fontSize: 16 }} />} title="Email follow-up">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                The system sends three emails. Write a different message for each one.
                Use {"{first_name}"} for their first name, {"{rep_name}"} for the rep, {"{rep_phone}"} for the rep’s phone, and {"{proposal_link}"} for the proposal page. If the rep has no phone, {"{rep_phone}"} uses the company profile phone.
              </Typography>
              <Tabs
                value={emailTab}
                onChange={(_event, value) => setEmailTab(value)}
                textColor="primary"
                indicatorColor="primary"
                aria-label="Email follow-up templates"
                sx={{
                  mb: 2,
                  minHeight: 40,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    textTransform: "uppercase",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    letterSpacing: "0.04em",
                    minHeight: 40,
                  },
                }}
              >
                <Tab label="1st email(7 days)" />
                <Tab label="2nd email(14 days)" />
                <Tab label="3rd email(21 days)" />
              </Tabs>
              <Stack spacing={2}>
                <TextField
                  label="Subject"
                  value={emails[emailTab]?.subject || ""}
                  onChange={(event) => updateEmail(emailTab, "subject", event.target.value)}
                  fullWidth
                  disabled={loading || isReadOnly}
                  inputProps={{ maxLength: 120 }}
                  sx={fieldSx}
                />
                <TextField
                  label="Message"
                  value={emails[emailTab]?.body || ""}
                  onChange={(event) => updateEmail(emailTab, "body", event.target.value)}
                  fullWidth
                  multiline
                  minRows={8}
                  disabled={loading || isReadOnly}
                  sx={fieldSx}
                />
              </Stack>
            </SectionCard>

            <SectionCard icon={<SmsOutlinedIcon sx={{ fontSize: 16 }} />} title="SMS follow-up">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                This is the text the customer receives. Use a short message. {"{first_name}"} is filled in when it sends.
              </Typography>
              <TextField
                label="Message"
                value={smsBody}
                onChange={(event) => setSmsBody(event.target.value.slice(0, SMS_HARD_LIMIT))}
                fullWidth
                multiline
                minRows={4}
                disabled={loading || isReadOnly}
                helperText={
                  smsBody.length > SMS_SOFT_LIMIT
                    ? `${smsBody.length} / ${SMS_SOFT_LIMIT} — texts over 160 characters may arrive as more than one message.`
                    : `${smsBody.length} / ${SMS_SOFT_LIMIT}`
                }
                sx={fieldSx}
              />
            </SectionCard>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/follow-up-intelligence")}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || saving || isReadOnly}
              onClick={handleSave}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
            >
              Save settings
            </Button>
          </Stack>
        </Box>
      </Box>

      <Dialog
        open={!!setupPrompt}
        onClose={() => setSetupPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Set up in Integrations</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", lineHeight: 1.55 }}>
            {setupPrompt === "sms"
              ? "SMS is not set up. An Admin can set it up in Integrations. Automatic texts stay off until then."
              : "Outgoing email is not set up. An Admin can set it up in Integrations. Automatic emails stay off until then."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSetupPrompt(null)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Close
          </Button>
          {role === "Admin" && (
            <Button
              variant="contained"
              onClick={() => navigate("/settings/integrations")}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
            >
              Go to Integrations
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
